import React, { useState, useEffect } from "react";
import {
  Upload,
  Loader2,
  FileDown,
  FileText,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TermsUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [latestFile, setLatestFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Load latest file on mount
  useEffect(() => {
    fetchLatestFile();
  }, []);

  /** Fetch the latest uploaded terms */
  const fetchLatestFile = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/terms/latest`, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!res.ok) {
        if (res.status !== 404) {
          console.error("Failed to fetch latest file:", res.statusText);
        }
        setLatestFile(null);
        return;
      }

      const data = await res.json();
      setLatestFile({
        id: data.id,
        filename: data.original_filename,
        uploadedAt: data.created_at,
      });
    } catch (err) {
      console.error("Error fetching latest file:", err);
    }
  };

  /** Upload PDF file to backend */
  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: "error", text: "Please select a file first." });
      return;
    }

    const formData = new FormData();
    formData.append("termsFile", file);

    try {
      setUploading(true);
      const res = await fetch(`${API_BASE_URL}/upload-terms`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.message || "Error uploading file");
      }

      setMessage({ type: "success", text: result.message });
      await fetchLatestFile();
      setFile(null);
    } catch (err) {
      console.error("Upload error:", err);
      setMessage({
        type: "error",
        text: err.message || "Error uploading file. Please try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  /** Handle drag/drop file input */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type !== "application/pdf") {
        setMessage({ type: "error", text: "Only PDF files are allowed." });
        return;
      }
      setFile(droppedFile);
      setMessage({ type: "", text: "" });
    }
  };

  /** Handle normal file input */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== "application/pdf") {
        setMessage({ type: "error", text: "Only PDF files are allowed." });
        return;
      }
      setFile(selectedFile);
      setMessage({ type: "", text: "" });
    }
  };

  /** Download file from backend */
  const handleDownload = async (id, filename) => {
    try {
      const res = await fetch(`${API_BASE_URL}/terms/download/${id}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "terms.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download error:", err);
      setMessage({ type: "error", text: "Failed to download file." });
    }
  };

  /** Format display helpers */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // === UI ===
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6 flex items-center justify-center">
      <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 max-w-2xl w-full">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Upload Terms & Conditions
          </h2>
          <p className="text-gray-600 text-sm">
            Upload your terms document in PDF format only
          </p>
        </div>

        {/* Drag & Drop Upload Area */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-8 mb-6 transition-all duration-200 ${
            dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-gray-50 hover:bg-gray-100"
          }`}
        >
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />

          <div className="text-center">
            <div className="mx-auto w-16 h-16 mb-4 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>

            {file ? (
              <div className="space-y-2">
                <p className="text-gray-900 font-semibold">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(file.size)}
                </p>
                <button
                  onClick={() => setFile(null)}
                  className="text-sm text-red-600 hover:text-red-700 underline"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <>
                <p className="text-gray-700 font-medium mb-2">
                  Drag and drop your file here
                </p>
                <p className="text-sm text-gray-500 mb-4">or</p>
                <span className="inline-block px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer">
                  Browse Files
                </span>
              </>
            )}
          </div>
        </div>

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={uploading || !file}
          className={`flex items-center justify-center w-full px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
            uploading || !file
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-5 w-5 mr-2" />
              Upload File
            </>
          )}
        </button>

        {/* Message Feedback */}
        {message.text && (
          <div
            className={`mt-6 p-4 rounded-xl flex items-start space-x-3 ${
              message.type === "success"
                ? "bg-green-50 border border-green-200"
                : "bg-red-50 border border-red-200"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <p
              className={`text-sm font-medium ${
                message.type === "success"
                  ? "text-green-800"
                  : "text-red-800"
              }`}
            >
              {message.text}
            </p>
          </div>
        )}

        {/* Latest Uploaded File Display */}
        {latestFile && (
          <div className="mt-8 p-6 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Current Terms Document
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {latestFile.filename}
                  </p>
                  <p className="text-xs text-gray-500">
                    Uploaded {formatDate(latestFile.uploadedAt)}
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  handleDownload(latestFile.id, latestFile.filename)
                }
                className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow"
              >
                <FileDown className="h-4 w-4" />
                <span className="text-sm font-medium">Download</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TermsUpload;
